/**
 * pdf.js, wired for this origin (U11, and the repair to it).
 *
 * The viewer's first attempt put a PDF in an `<iframe sandbox>` with no `allow-scripts`.
 * That is the correct security posture and it does not render: Chrome's built-in PDF viewer
 * is itself a scripted document, so a frame with scripting taken away draws a broken-file
 * icon. Measured in a real browser across four sandbox values — unsandboxed renders, every
 * sandboxed variant fails. Weakening the sandbox was never on the table: a PDF can carry
 * JavaScript and this origin holds the Clerk session and the invitation token.
 *
 * pdf.js resolves that. It *parses* the PDF and paints it onto a `<canvas>`; it never
 * executes the document's own JavaScript. The guarantee is kept by construction rather than
 * by an attribute, and the page actually appears.
 *
 * Four things here are load-bearing, and each is a line in the CSP that did not have to move:
 *
 * 1. **The bytes are passed in, never a URL.** `getDocument({ data })` and never
 *    `getDocument({ url })`. A URL would make pdf.js issue its own `fetch` with no
 *    `Authorization` header — a 401, and the obvious repair (`?token=`) is the Clerk-JWT
 *    leak the whole of `api.ts` is written around. The caller has already fetched these
 *    bytes with the bearer token.
 * 2. **The worker is a same-origin asset.** `?url` makes Vite emit `pdf.worker.min.mjs`
 *    into the build's own asset directory and hand back a root-relative path, so
 *    `worker-src 'self' blob:` already allows it. A CDN `workerSrc` — which most pdf.js
 *    setup guides reach for first — would need a host added to the CSP, and none is.
 * 3. **`useWasm: false`.** pdf.js 6 uses WebAssembly for JBIG2/JPEG2000 image decoding, ICC
 *    colour management and PostScript (type 4) function evaluation. Compiling WASM needs
 *    `'wasm-unsafe-eval'` in `script-src`, which this origin does not grant and will not.
 *    Every one of those paths has a JavaScript fallback that pdf.js takes on its own, so
 *    switching WASM off is a configuration choice and not a missing feature. pdf.js 6 has
 *    no `eval` and no `new Function` anywhere in either build — checked, not assumed — so
 *    `unsafe-eval` is not needed in any configuration.
 * 4. **`pdfjs-dist` is behind a dynamic `import()`.** It is ~430 kB minified and a teacher
 *    who never opens a PDF should never pay for it. The build splits it into its own chunk;
 *    the SPA's entry bundle carries only the worker's path.
 *
 * A fifth thing arrived with the text layer. pdf.js paints pixels, so a page it has drawn is
 * unselectable and silent to a screen reader until a *second* layer of positioned, transparent
 * spans is laid over the canvas. `TextLayer` is pdf.js's own and is used as-is: it sizes its
 * container with `round(down, var(--total-scale-factor) * <pageWidth>px, var(--scale-round-x))`
 * and positions every span as a *percentage* of the page, so the whole layer is driven by one
 * CSS variable and stays pinned to the canvas at any zoom. Those two custom properties are
 * therefore not decoration — a page box that does not define them has an unsized text layer.
 *
 * The types below are the seam. They are the subset of pdf.js the viewer actually touches,
 * so a test can hand the component a fake and assert what it was given — that it received
 * bytes, that the document was destroyed, that an in-flight render was cancelled — without
 * a real PDF parser inside jsdom.
 */

// Vite emits this as a build asset and resolves it to a same-origin path. It is a string,
// not the worker's code: the 1.2 MB file is never part of any JavaScript chunk.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/** The one thing the viewer reads off a viewport, and the shape it hands back to `render`. */
export interface PdfViewport {
  readonly width: number;
  readonly height: number;
}

/** A paint in progress. Cancelling one is the difference between a torn-down canvas and a crash. */
export interface PdfRenderTask {
  readonly promise: Promise<void>;
  cancel(): void;
}

/**
 * One run of text off the page, as pdf.js reports it.
 *
 * Only `str` is named, because only `str` is this package's business: the viewer counts the
 * runs to know whether a page has any selectable text at all, and hands the whole object
 * straight back to pdf.js for positioning. The `transform` maths — the six-number matrix, the
 * ascent lookup, the horizontal scale measured against a canvas — stays inside pdf.js, which
 * is the argument for using its `TextLayer` rather than reimplementing it here.
 */
export interface PdfTextItem {
  /** The run's characters. Absent on the marked-content markers pdf.js interleaves. */
  readonly str?: string;
  /** `beginMarkedContent` and friends, which carry structure rather than text. */
  readonly type?: string;
}

export interface PdfTextContent {
  readonly items: readonly PdfTextItem[];
}

export interface PdfPage {
  /** `offsetX`/`offsetY` shift the page in device pixels; a passage snapshot paints a crop. */
  getViewport(params: { scale: number; offsetX?: number; offsetY?: number }): PdfViewport;
  render(params: { canvas: HTMLCanvasElement; viewport: PdfViewport }): PdfRenderTask;
  /** The page's text runs. Fetched by the viewer and handed back to `renderTextLayer`. */
  getTextContent(): Promise<PdfTextContent>;
  /**
   * Fill `container` with one positioned, transparent `<span>` per text run, laid out for
   * `viewport`. This is what makes a painted page selectable and readable by a screen reader;
   * without it the canvas is pixels and nothing else.
   */
  renderTextLayer(params: {
    container: HTMLElement;
    viewport: PdfViewport;
    textContent: PdfTextContent;
  }): PdfRenderTask;
}

export interface PdfDocument {
  readonly numPages: number;
  getPage(pageNumber: number): Promise<PdfPage>;
  /**
   * pdf.js holds a dedicated Web Worker and a transport per document. Dropping the reference
   * frees neither — this is the `URL.revokeObjectURL` of the PDF path, and the viewer calls
   * it on unmount and on a change of material for exactly the same reason.
   */
  destroy(): Promise<void>;
}

export interface PdfEngine {
  /** Parse bytes. Never a URL: pdf.js must not make its own unauthenticated request. */
  load(bytes: ArrayBuffer): Promise<PdfDocument>;
}

/** Exported so a test can assert the worker is same-origin rather than a CDN. */
export const pdfWorkerSrc: string = workerUrl;

export const pdfJsEngine: PdfEngine = {
  async load(bytes) {
    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

    const task = pdfjs.getDocument({
      // `data`, and no `url`. See (1) above.
      data: bytes,
      // See (3). Every WASM path in pdf.js 6 has a JS fallback; `script-src` stays as it is.
      useWasm: false,
      // A material is at most 10 MB and already in memory. Range requests and streaming are
      // about fetching, and nothing here fetches.
      disableRange: true,
      disableStream: true,
    });

    const doc = await task.promise;

    /**
     * pdf.js's own page, narrowed to the four things the viewer asks of it.
     *
     * `TextLayer` is pdf.js's, deliberately. Positioning a text run means composing the
     * viewport's matrix with the run's own, taking the font's ascent from a measured canvas
     * and scaling the span horizontally until its rendered width matches the width the PDF
     * declares. Reimplementing that is how text layers end up a few pixels adrift, which is
     * precisely the bug a highlight cannot survive. The viewport handed over is a real
     * `PageViewport` — `TextLayer` reads `rawDims` off it — so it is passed through rather
     * than reconstructed.
     */
    const wrap = (page: Awaited<ReturnType<typeof doc.getPage>>): PdfPage => ({
      getViewport: (params) => page.getViewport(params),
      render: ({ canvas, viewport }) =>
        page.render({ canvas, viewport: viewport as never }),
      getTextContent: () => page.getTextContent(),
      renderTextLayer: ({ container, viewport, textContent }) => {
        const layer = new pdfjs.TextLayer({
          textContentSource: textContent as never,
          container,
          viewport: viewport as never,
        });
        return { promise: layer.render(), cancel: () => { layer.cancel(); } };
      },
    });

    return {
      numPages: doc.numPages,
      getPage: async (pageNumber) => wrap(await doc.getPage(pageNumber)),
      // `PDFDocumentProxy` has no `destroy` in pdf.js 6; the loading task owns the worker.
      destroy: () => task.destroy(),
    };
  },
};
